<template>
    <v-card class="pa-5">
        <v-form v-model="valid" @submit.prevent="submit" ref="form" autocomplete="new-password">
            <v-alert v-if="info" color="blue" outlined text v-html="info" />
            <v-alert v-if="error" color="red" icon="mdi-alert-octagon-outline" outlined text>
                {{ error }}
            </v-alert>
            <v-alert v-else-if="message" color="green" outlined text>
                <v-progress-circular indeterminate color="green" />
                {{ message }}
            </v-alert>

            <v-row no-gutters style="gap: 8px">
                <v-col>
                    <div class="font-weight-medium">First name</div>
                    <v-text-field outlined dense v-model="form.FirstName" :rules="[$validators.notEmpty]" />
                </v-col>
                <v-col>
                    <div class="font-weight-medium">Last name</div>
                    <v-text-field outlined dense v-model="form.LastName" :rules="[$validators.notEmpty]" />
                </v-col>
            </v-row>

            <div class="font-weight-medium">Email</div>
            <v-text-field outlined dense v-model="form.Email" type="email" :rules="[$validators.isEmail]" :readonly="emailReadOnly" />

            <div class="font-weight-medium">Password</div>
            <v-text-field outlined dense v-model="form.Password" type="password" :rules="[$validators.isPassword]" />

            <div class="d-flex align-center justify-space-between">
                <p class="mb-0">
                    By signing up, you agree to the
                    <a href="/terms" target="_blank">Terms of Service</a>
                    and
                    <a href="/privacy" target="_blank">Privacy Policy</a>
                </p>
                <div v-if="recaptchaSiteKey" id="recaptcha-badge"></div>
            </div>
            
            <v-btn block type="submit" :disabled="!valid || loadingGoogle" :loading="loading" class="blue-btn mt-5">Sign Up</v-btn>
        </v-form>

        <div class="my-5 text-center">or</div>

        <v-btn block @click="google" outlined>
            <img src="/static/img/google.svg" height="20" alt="google icon" style="position: absolute; left: 0" />
            Sign up with Google
            <v-progress-circular v-if="loadingGoogle" indeterminate size="20" color="green" class="ml-2" />
        </v-btn>

        <div class="pt-12">
            <p class="text-center">
                Already have an account?
                <router-link :to="{ name: 'signin' }" class="text-no-wrap">Log In.</router-link>
            </p>
        </div>
    </v-card>
</template>

<script>
export default {
    data() {
        return {
            form: {
                Email: '',
                Password: '',
                FirstName: '',
                LastName: '',
                Invite: '',
            },
            valid: false,
            error: '',
            message: '',
            info: '',
            loading: false,
            emailReadOnly: false,
            loadingGoogle: false,
            recaptchaToken: '',
            recaptchaSiteKey: (window.Coroot && window.Coroot.RecaptchaSiteKey) || '',
            recaptcha: null,
        };
    },

    mounted() {
        if (this.recaptchaSiteKey) {
            import('recaptcha-v3').then(({ load }) => {
                return load(this.recaptchaSiteKey);
            }).then((recaptcha) => {
                this.recaptcha = recaptcha;
                this.$nextTick(() => {
                    const badge = document.querySelector('.grecaptcha-badge');
                    const container = document.getElementById('recaptcha-badge');
                    if (badge && container) {
                        container.appendChild(badge);
                    }
                });
            }).catch(() => {
                this.error = 'Failed to load reCAPTCHA. Please refresh and try again.';
            });
        }
        if (this.$route.query.invite) {
            const token = this.$route.query.invite || '';
            this.form.Invite = token;
            const info = this.$api.jwtDecode(token);
            this.form.Email = info.e;
            this.emailReadOnly = true;
            this.info = `Sign up to join the <b>${info.wn}</b> workspace`;
        }
        if (this.$route.query.google) {
            const req = {
                Action: 'signup',
                RedirectURL: window.location.origin + this.$router.resolve({ name: 'auth/google' }).route.path,
                AuthCode: this.$route.query.google,
                Invite: this.$route.query.invite || '',
            };
            this.loadingGoogle = true;
            this.$api.authGoogle(req, (data, meta) => {
                this.loadingGoogle = false;
                if (meta.error) {
                    this.error = meta.error;
                    return;
                }
                this.redirect();
            });
        }
    },

    methods: {
        redirect() {
            this.$router.push({ name: 'account', query: { ...this.$route.query, google: undefined } }).catch((err) => err);
        },
        submit() {
            if (!this.recaptcha) {
                this.error = 'reCAPTCHA not loaded. Please refresh and try again.';
                return;
            }
            
            this.loading = true;
            this.recaptcha.execute('submit').then((token) => {
                const formData = { ...this.form, recaptchaToken: token };
                
                this.$api.register(formData, (data, meta) => {
                    this.loading = false;
                    if (meta.code === 409) {
                        this.error = 'Email is already being used.';
                        return;
                    }
                    if (meta.error) {
                        this.error = meta.error;
                        return;
                    }
                    this.redirect();
                });
            }).catch(() => {
                this.loading = false;
                this.error = 'reCAPTCHA verification failed. Please try again.';
            });
        },
        google() {
            const req = {
                State: JSON.stringify({ action: this.$route.name, params: this.$route.query }),
                RedirectURL: window.location.origin + this.$router.resolve({ name: 'auth/google' }).route.path,
                LoginHint: this.emailReadOnly ? this.form.Email : '',
            };
            this.loadingGoogle = true;
            this.$api.authGoogle(req, (data, meta) => {
                this.loadingGoogle = false;
                if (meta.error) {
                    this.error = meta.error;
                    return;
                }
                window.location.href = data;
            });
        },
    },
};
</script>

<style scoped>
#recaptcha-badge ::v-deep .grecaptcha-badge {
    position: static !important;
    transform: none !important;
    margin: 0 !important;
}
</style>
